/**
 * Procedural avatar look — shared by protocol, engine, and AvatarStudio.
 * Flat wire shape for multiplayer; normalize() fills defaults + migrates v1.
 */

export type AvatarLook = {
  shirt: string;
  hair: string;
  skin: string;
  /** Hair silhouette (not hat). */
  style: string;
  pants: string;
  shoes: string;
  /** none | cap | beanie | headband | headphones | hardhat | beret | visor | bandana | … */
  hat: string;
  hatColor: string;
  /** none | glasses | round | shades | half | monocle | sports | star */
  glasses: string;
  /** none | stubble | beard | mustache | goatee */
  facialHair: string;
  /** none | earrings | necklace | watch | tie | scarf | lanyard | badge | ring */
  jewelry: string;
  /** solid | tee | polo | hoodie | stripes | blazer | vest | sweater | flannel | tank */
  topStyle: string;
  /** solid | jeans | shorts | skirt | cargo */
  bottomStyle: string;
  /** sneakers | boots | loafers | sandals */
  shoeStyle: string;
  /** Hex pupil color */
  eyeColor: string;
  /** neutral | smile | grin | serious */
  expression: string;
  /** none | tote | messenger | laptop-bag */
  backpack: string;
};

export const DEFAULT_AVATAR_LOOK: AvatarLook = {
  shirt: "#1E6CC7",
  hair: "#2B2118",
  skin: "#E9BE93",
  style: "short",
  pants: "#1F2A44",
  shoes: "#001847",
  hat: "none",
  hatColor: "#1E6CC7",
  glasses: "none",
  facialHair: "none",
  jewelry: "none",
  topStyle: "solid",
  bottomStyle: "solid",
  shoeStyle: "sneakers",
  eyeColor: "#101728",
  expression: "neutral",
  backpack: "none",
};

/** @deprecated alias — prefer DEFAULT_AVATAR_LOOK */
export const DEFAULT_AVATAR_COLORS = DEFAULT_AVATAR_LOOK;

export const SKIN_SWATCHES = [
  "#F5D7B8",
  "#E9BE93",
  "#D4A574",
  "#B97F4F",
  "#8D5524",
  "#7C4A26",
  "#5C3A21",
  "#3D2314",
] as const;

export const HAIR_SWATCHES = [
  "#2B2118",
  "#5A3A22",
  "#C9A227",
  "#8894A8",
  "#101010",
  "#E85D04",
  "#9B5DE5",
  "#E63946",
  "#FFFFFF",
] as const;

export const SHIRT_SWATCHES = [
  "#1E6CC7",
  "#2D9BD6",
  "#64479D",
  "#2EA86A",
  "#E8A93C",
  "#D8443C",
  "#001847",
  "#FFFFFF",
  "#FF6B9D",
  "#1F2A44",
  "#F4A261",
] as const;

export const PANTS_SWATCHES = [
  "#1F2A44",
  "#2C3E50",
  "#4A5568",
  "#5B7C99",
  "#6B5B4F",
  "#3D5A40",
  "#5C4033",
  "#101010",
] as const;

export const SHOE_SWATCHES = [
  "#001847",
  "#101010",
  "#5C4033",
  "#FFFFFF",
  "#D8443C",
  "#2D9BD6",
  "#8894A8",
] as const;

export const HAT_COLOR_SWATCHES = SHIRT_SWATCHES;

export const HAIR_STYLE_OPTIONS = [
  { id: "none", label: "None" },
  { id: "short", label: "Short" },
  { id: "long", label: "Long" },
  { id: "bun", label: "Bun" },
  { id: "afro", label: "Afro" },
  { id: "fade", label: "Fade" },
  { id: "ponytail", label: "Ponytail" },
  { id: "side-part", label: "Side part" },
  { id: "curly", label: "Curly" },
  { id: "mohawk", label: "Mohawk" },
  { id: "spiky", label: "Spiky" },
  { id: "braids", label: "Braids" },
  { id: "locs", label: "Locs" },
  { id: "buzz", label: "Buzz" },
  { id: "wavy", label: "Wavy" },
] as const;

export const HAT_OPTIONS = [
  { id: "none", label: "None" },
  { id: "cap", label: "Cap" },
  { id: "beanie", label: "Beanie" },
  { id: "headband", label: "Headband" },
  { id: "headphones", label: "Headphones" },
  { id: "hardhat", label: "Hard hat" },
  { id: "beret", label: "Beret" },
  { id: "visor", label: "Visor" },
  { id: "bandana", label: "Bandana" },
  { id: "crown", label: "Crown" },
  { id: "flower", label: "Flower" },
  { id: "cowboy", label: "Cowboy" },
  { id: "propeller", label: "Propeller" },
] as const;

export const GLASSES_OPTIONS = [
  { id: "none", label: "None" },
  { id: "glasses", label: "Square" },
  { id: "round", label: "Round" },
  { id: "shades", label: "Shades" },
  { id: "half", label: "Half-rim" },
  { id: "monocle", label: "Monocle" },
  { id: "sports", label: "Sports" },
  { id: "star", label: "Star shades" },
] as const;

export const FACIAL_HAIR_OPTIONS = [
  { id: "none", label: "None" },
  { id: "stubble", label: "Stubble" },
  { id: "mustache", label: "Mustache" },
  { id: "beard", label: "Beard" },
  { id: "goatee", label: "Goatee" },
] as const;

export const JEWELRY_OPTIONS = [
  { id: "none", label: "None" },
  { id: "earrings", label: "Earrings" },
  { id: "necklace", label: "Necklace" },
  { id: "watch", label: "Watch" },
  { id: "tie", label: "Tie" },
  { id: "scarf", label: "Scarf" },
  { id: "lanyard", label: "Lanyard" },
  { id: "badge", label: "Badge" },
  { id: "ring", label: "Ring" },
] as const;

export const TOP_STYLE_OPTIONS = [
  { id: "solid", label: "Solid" },
  { id: "tee", label: "Tee" },
  { id: "polo", label: "Polo" },
  { id: "hoodie", label: "Hoodie" },
  { id: "stripes", label: "Stripes" },
  { id: "blazer", label: "Blazer" },
  { id: "vest", label: "Vest" },
  { id: "sweater", label: "Sweater" },
  { id: "flannel", label: "Flannel" },
  { id: "tank", label: "Tank" },
] as const;

export const BOTTOM_STYLE_OPTIONS = [
  { id: "solid", label: "Solid" },
  { id: "jeans", label: "Jeans" },
  { id: "shorts", label: "Shorts" },
  { id: "skirt", label: "Skirt" },
  { id: "cargo", label: "Cargo" },
] as const;

export const SHOE_STYLE_OPTIONS = [
  { id: "sneakers", label: "Sneakers" },
  { id: "boots", label: "Boots" },
  { id: "loafers", label: "Loafers" },
  { id: "sandals", label: "Sandals" },
] as const;

export const EYE_COLOR_SWATCHES = [
  "#101728",
  "#3D2314",
  "#2D5A87",
  "#2EA86A",
  "#8B6914",
  "#5B7C99",
] as const;

export const EXPRESSION_OPTIONS = [
  { id: "neutral", label: "Neutral" },
  { id: "smile", label: "Smile" },
  { id: "grin", label: "Grin" },
  { id: "serious", label: "Serious" },
] as const;

export const BACKPACK_OPTIONS = [
  { id: "none", label: "None" },
  { id: "tote", label: "Tote" },
  { id: "messenger", label: "Messenger" },
  { id: "laptop-bag", label: "Laptop bag" },
] as const;

/** One-tap outfit partials — applied with current skin preserved. */
export const OUTFIT_PRESETS = [
  {
    id: "engineer",
    label: "Engineer",
    look: {
      shirt: "#1E6CC7",
      hair: "#2B2118",
      style: "short",
      pants: "#1F2A44",
      shoes: "#001847",
      hat: "none",
      hatColor: "#1E6CC7",
      glasses: "glasses",
      facialHair: "none",
      jewelry: "lanyard",
      topStyle: "polo",
      bottomStyle: "jeans",
      shoeStyle: "sneakers",
      eyeColor: "#2D5A87",
      expression: "neutral",
      backpack: "laptop-bag",
    },
  },
  {
    id: "leader",
    label: "Leader",
    look: {
      shirt: "#001847",
      hair: "#101010",
      style: "side-part",
      pants: "#1F2A44",
      shoes: "#101010",
      hat: "none",
      hatColor: "#001847",
      glasses: "none",
      facialHair: "none",
      jewelry: "tie",
      topStyle: "blazer",
      bottomStyle: "solid",
      shoeStyle: "loafers",
      eyeColor: "#3D2314",
      expression: "serious",
      backpack: "none",
    },
  },
  {
    id: "party",
    label: "Party",
    look: {
      shirt: "#FF6B9D",
      hair: "#9B5DE5",
      style: "spiky",
      pants: "#64479D",
      shoes: "#E8A93C",
      hat: "visor",
      hatColor: "#FF6B9D",
      glasses: "shades",
      facialHair: "none",
      jewelry: "earrings",
      topStyle: "tee",
      bottomStyle: "shorts",
      shoeStyle: "sneakers",
      eyeColor: "#101728",
      expression: "grin",
      backpack: "none",
    },
  },
  {
    id: "focus",
    label: "Focus",
    look: {
      shirt: "#2C3E50",
      hair: "#5A3A22",
      style: "bun",
      pants: "#4A5568",
      shoes: "#101010",
      hat: "headphones",
      hatColor: "#2D9BD6",
      glasses: "round",
      facialHair: "none",
      jewelry: "watch",
      topStyle: "hoodie",
      bottomStyle: "solid",
      shoeStyle: "sneakers",
      eyeColor: "#3D2314",
      expression: "serious",
      backpack: "tote",
    },
  },
  {
    id: "casual",
    label: "Casual",
    look: {
      shirt: "#2EA86A",
      hair: "#5A3A22",
      style: "curly",
      pants: "#5B7C99",
      shoes: "#FFFFFF",
      hat: "cap",
      hatColor: "#2EA86A",
      glasses: "none",
      facialHair: "stubble",
      jewelry: "none",
      topStyle: "stripes",
      bottomStyle: "jeans",
      shoeStyle: "sneakers",
      eyeColor: "#2D5A87",
      expression: "smile",
      backpack: "none",
    },
  },
  {
    id: "creative",
    label: "Creative",
    look: {
      shirt: "#9B5DE5",
      hair: "#E85D04",
      style: "wavy",
      pants: "#1F2A44",
      shoes: "#FF6B9D",
      hat: "beret",
      hatColor: "#101010",
      glasses: "round",
      facialHair: "none",
      jewelry: "earrings",
      topStyle: "sweater",
      bottomStyle: "skirt",
      shoeStyle: "boots",
      eyeColor: "#2EA86A",
      expression: "smile",
      backpack: "tote",
    },
  },
  {
    id: "athlete",
    label: "Athlete",
    look: {
      shirt: "#E63946",
      hair: "#101010",
      style: "buzz",
      pants: "#1F2A44",
      shoes: "#FFFFFF",
      hat: "visor",
      hatColor: "#E63946",
      glasses: "sports",
      facialHair: "none",
      jewelry: "watch",
      topStyle: "tank",
      bottomStyle: "shorts",
      shoeStyle: "sneakers",
      eyeColor: "#101728",
      expression: "grin",
      backpack: "none",
    },
  },
  {
    id: "founder",
    label: "Founder",
    look: {
      shirt: "#F4A261",
      hair: "#2B2118",
      style: "locs",
      pants: "#5C4033",
      shoes: "#001847",
      hat: "none",
      hatColor: "#F4A261",
      glasses: "none",
      facialHair: "goatee",
      jewelry: "badge",
      topStyle: "flannel",
      bottomStyle: "cargo",
      shoeStyle: "boots",
      eyeColor: "#3D2314",
      expression: "smile",
      backpack: "messenger",
    },
  },
  {
    id: "nightowl",
    label: "Night owl",
    look: {
      shirt: "#64479D",
      hair: "#8894A8",
      style: "fade",
      pants: "#101010",
      shoes: "#2D9BD6",
      hat: "headphones",
      hatColor: "#9B5DE5",
      glasses: "star",
      facialHair: "stubble",
      jewelry: "ring",
      topStyle: "hoodie",
      bottomStyle: "solid",
      shoeStyle: "sneakers",
      eyeColor: "#5B7C99",
      expression: "neutral",
      backpack: "laptop-bag",
    },
  },
  {
    id: "remote",
    label: "Remote",
    look: {
      shirt: "#2D9BD6",
      hair: "#5A3A22",
      style: "bun",
      pants: "#4A5568",
      shoes: "#FFFFFF",
      hat: "none",
      hatColor: "#2D9BD6",
      glasses: "half",
      facialHair: "none",
      jewelry: "watch",
      topStyle: "hoodie",
      bottomStyle: "jeans",
      shoeStyle: "sandals",
      eyeColor: "#2D5A87",
      expression: "smile",
      backpack: "none",
    },
  },
  {
    id: "intern",
    label: "Intern",
    look: {
      shirt: "#FFFFFF",
      hair: "#C9A227",
      style: "short",
      pants: "#5B7C99",
      shoes: "#101010",
      hat: "none",
      hatColor: "#1E6CC7",
      glasses: "none",
      facialHair: "none",
      jewelry: "lanyard",
      topStyle: "tee",
      bottomStyle: "jeans",
      shoeStyle: "sneakers",
      eyeColor: "#3D2314",
      expression: "grin",
      backpack: "tote",
    },
  },
  {
    id: "designer",
    label: "Designer",
    look: {
      shirt: "#1F2A44",
      hair: "#101010",
      style: "side-part",
      pants: "#101010",
      shoes: "#FFFFFF",
      hat: "none",
      hatColor: "#1F2A44",
      glasses: "round",
      facialHair: "none",
      jewelry: "earrings",
      topStyle: "blazer",
      bottomStyle: "solid",
      shoeStyle: "loafers",
      eyeColor: "#8B6914",
      expression: "serious",
      backpack: "messenger",
    },
  },
] as const;

export type OutfitPresetId = (typeof OUTFIT_PRESETS)[number]["id"];

export type AvatarStudioTabId =
  | "skin"
  | "hair"
  | "top"
  | "bottom"
  | "shoes"
  | "head"
  | "face"
  | "flair";

export const AVATAR_STUDIO_TABS: ReadonlyArray<{ id: AvatarStudioTabId; label: string }> = [
  { id: "skin", label: "Skin" },
  { id: "hair", label: "Hair" },
  { id: "top", label: "Top" },
  { id: "bottom", label: "Bottom" },
  { id: "shoes", label: "Shoes" },
  { id: "head", label: "Head" },
  { id: "face", label: "Face" },
  { id: "flair", label: "Flair" },
];

const HAIR_STYLE_IDS: Set<string> = new Set(HAIR_STYLE_OPTIONS.map((o) => o.id));
const HAT_IDS: Set<string> = new Set(HAT_OPTIONS.map((o) => o.id));
const GLASSES_IDS: Set<string> = new Set(GLASSES_OPTIONS.map((o) => o.id));
const FACIAL_IDS: Set<string> = new Set(FACIAL_HAIR_OPTIONS.map((o) => o.id));
const JEWELRY_IDS: Set<string> = new Set(JEWELRY_OPTIONS.map((o) => o.id));
const TOP_STYLE_IDS: Set<string> = new Set(TOP_STYLE_OPTIONS.map((o) => o.id));
const BOTTOM_STYLE_IDS: Set<string> = new Set(BOTTOM_STYLE_OPTIONS.map((o) => o.id));
const SHOE_STYLE_IDS: Set<string> = new Set(SHOE_STYLE_OPTIONS.map((o) => o.id));
const EXPRESSION_IDS: Set<string> = new Set(EXPRESSION_OPTIONS.map((o) => o.id));
const BACKPACK_IDS: Set<string> = new Set(BACKPACK_OPTIONS.map((o) => o.id));
const EYE_COLOR_IDS: Set<string> = new Set(EYE_COLOR_SWATCHES);

function str(v: unknown, fallback: string): string {
  return typeof v === "string" && v.length > 0 && v.length <= 32 ? v : fallback;
}

function enumOr(v: unknown, allowed: Set<string>, fallback: string): string {
  const s = typeof v === "string" ? v : "";
  return allowed.has(s) ? s : fallback;
}

/**
 * Normalize any partial / v1 payload into a full AvatarLook.
 * v1 `style: "cap"` → hat cap + short hair.
 */
export function normalizeAvatarLook(raw: unknown): AvatarLook {
  const o =
    raw && typeof raw === "object"
      ? (raw as Record<string, unknown>)
      : ({} as Record<string, unknown>);

  let style = str(o.style, DEFAULT_AVATAR_LOOK.style);
  let hat = str(o.hat, DEFAULT_AVATAR_LOOK.hat);

  // Migrate old hair style that was actually a hat
  if (style === "cap") {
    if (hat === "none" || !HAT_IDS.has(hat)) hat = "cap";
    style = "short";
  }
  if (!HAIR_STYLE_IDS.has(style)) style = "short";
  if (!HAT_IDS.has(hat)) hat = "none";

  return {
    shirt: str(o.shirt, DEFAULT_AVATAR_LOOK.shirt),
    hair: str(o.hair, DEFAULT_AVATAR_LOOK.hair),
    skin: str(o.skin, DEFAULT_AVATAR_LOOK.skin),
    style,
    pants: str(o.pants, DEFAULT_AVATAR_LOOK.pants),
    shoes: str(o.shoes, DEFAULT_AVATAR_LOOK.shoes),
    hat,
    hatColor: str(o.hatColor, DEFAULT_AVATAR_LOOK.hatColor),
    glasses: enumOr(o.glasses, GLASSES_IDS, "none"),
    facialHair: enumOr(o.facialHair, FACIAL_IDS, "none"),
    jewelry: enumOr(o.jewelry, JEWELRY_IDS, "none"),
    topStyle: enumOr(o.topStyle, TOP_STYLE_IDS, DEFAULT_AVATAR_LOOK.topStyle),
    bottomStyle: enumOr(o.bottomStyle, BOTTOM_STYLE_IDS, DEFAULT_AVATAR_LOOK.bottomStyle),
    shoeStyle: enumOr(o.shoeStyle, SHOE_STYLE_IDS, DEFAULT_AVATAR_LOOK.shoeStyle),
    eyeColor: enumOr(o.eyeColor, EYE_COLOR_IDS, DEFAULT_AVATAR_LOOK.eyeColor),
    expression: enumOr(o.expression, EXPRESSION_IDS, DEFAULT_AVATAR_LOOK.expression),
    backpack: enumOr(o.backpack, BACKPACK_IDS, DEFAULT_AVATAR_LOOK.backpack),
  };
}

/**
 * Apply an outfit preset, preserving the wearer's skin tone.
 */
export function applyOutfitPreset(
  current: AvatarLook | unknown,
  presetId: OutfitPresetId | string,
): AvatarLook {
  const base = normalizeAvatarLook(current);
  const preset = OUTFIT_PRESETS.find((p) => p.id === presetId);
  if (!preset) return base;
  return normalizeAvatarLook({
    ...base,
    ...preset.look,
    skin: base.skin,
  });
}

export function pickRandom<T>(arr: readonly T[]): T {
  const i = Math.floor(Math.random() * arr.length);
  const v = arr[i];
  if (v !== undefined) return v;
  return arr[0] as T;
}

export function randomAvatarLook(): AvatarLook {
  return {
    shirt: pickRandom(SHIRT_SWATCHES),
    hair: pickRandom(HAIR_SWATCHES),
    skin: pickRandom(SKIN_SWATCHES),
    style: pickRandom(HAIR_STYLE_OPTIONS).id,
    pants: pickRandom(PANTS_SWATCHES),
    shoes: pickRandom(SHOE_SWATCHES),
    hat: pickRandom(HAT_OPTIONS).id,
    hatColor: pickRandom(HAT_COLOR_SWATCHES),
    glasses: pickRandom(GLASSES_OPTIONS).id,
    facialHair: pickRandom(FACIAL_HAIR_OPTIONS).id,
    jewelry: pickRandom(JEWELRY_OPTIONS).id,
    topStyle: pickRandom(TOP_STYLE_OPTIONS).id,
    bottomStyle: pickRandom(BOTTOM_STYLE_OPTIONS).id,
    shoeStyle: pickRandom(SHOE_STYLE_OPTIONS).id,
    eyeColor: pickRandom(EYE_COLOR_SWATCHES),
    expression: pickRandom(EXPRESSION_OPTIONS).id,
    backpack: pickRandom(BACKPACK_OPTIONS).id,
  };
}
